import { useEffect, useRef, useState } from "react";
import {
  isOptionSource,
  type ListFilter,
  type ListFilterOption,
} from "@/base/list/types";

// Each filter's options, with the ones PMS supplies read once when the list
// mounts (BRK-S254). Until those arrive a filter has nothing to check a URL
// value against, so useListUrlState holds that value and the list waits.

/** A filter's options: known, still being read, or unreadable. */
export type FilterOptions = readonly ListFilterOption[] | "loading" | "failed";

export function useFilterOptions(
  filters: readonly ListFilter[] | undefined,
): Record<string, FilterOptions> {
  const sourced = (filters ?? []).flatMap((f) =>
    isOptionSource(f.options)
      ? [{ apiParam: f.apiParam, source: f.options }]
      : [],
  );
  // A page may rebuild its definition on every render, so the read keys on
  // what it reads, never on the loader's identity.
  const key = sourced.map((s) => `${s.apiParam}:${s.source.endpoint}`).join();
  const latest = useRef(sourced);
  latest.current = sourced;

  const [read, setRead] = useState<{
    key: string;
    options: Record<string, readonly ListFilterOption[] | "failed">;
  }>({ key, options: {} });

  useEffect(() => {
    let active = true;
    const settle = (
      apiParam: string,
      options: readonly ListFilterOption[] | "failed",
    ) => {
      if (!active) return;
      setRead((prev) => ({
        key,
        options: {
          ...(prev.key === key ? prev.options : {}),
          [apiParam]: options,
        },
      }));
    };
    for (const { apiParam, source } of latest.current) {
      source.load().then(
        (options) => settle(apiParam, options),
        () => settle(apiParam, "failed"),
      );
    }
    return () => {
      active = false;
    };
  }, [key]);

  const known = read.key === key ? read.options : {};
  return Object.fromEntries(
    (filters ?? []).map((f) => [
      f.apiParam,
      isOptionSource(f.options) ? (known[f.apiParam] ?? "loading") : f.options,
    ]),
  );
}
